import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RIGHT_ALIGNED } from 'conversations-internal-schema/widget-location/constants/WidgetLocations';
import { useDispatch, useSelector } from 'react-redux';
import { getLauncherType } from '../widget-data/selectors/widgetDataSelectors';
import { useBrowserWindowContext } from '../components/BrowserWindowContext';
import { handleIframeResize, expandIframeToViewportForDrag } from '../post-message/handleIframeResize';
import { postMessageToParent } from '../post-message/postMessageToParent';
import { clampWidgetSize } from '../widget-size/widgetSizePreference';
import { STORE_WIDGET_SIZE_PREFERENCE, WIDGET_SIZE_PREFERENCE } from '../constants/PostMessageTypes';
import { trackInteraction } from '../usage-tracking/actions/trackInteraction';
import { EVENT_NAMES } from '../usage-tracking/constants/eventNames';
const CHAT_WINDOW_CLASS = '.chat-widget';
export const useWidgetResize = ({
  isResizeEnabled,
  widgetLocation,
  defaultWidgetSize
}) => {
  const {
    browserWindowHeight,
    browserWindowWidth
  } = useBrowserWindowContext();
  const launcherType = useSelector(getLauncherType);
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [savedSizePreference, setSavedSizePreference] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const isExpanded = savedSizePreference !== null && (savedSizePreference.width > defaultWidgetSize.width || savedSizePreference.height > defaultWidgetSize.height);
  useEffect(() => {
    if (!isResizeEnabled) return;
    const handleMessage = event => {
      var _parsed, _parsed$data;
      let parsed = null;
      try {
        parsed = JSON.parse(event.data);
      } catch (_unused) {
        return;
      }
      if (((_parsed = parsed) === null || _parsed === void 0 ? void 0 : _parsed.type) !== WIDGET_SIZE_PREFERENCE) return;
      const {
        width,
        height
      } = (_parsed$data = parsed.data) !== null && _parsed$data !== void 0 ? _parsed$data : {};
      if (Number.isFinite(width) && Number.isFinite(height)) {
        setSavedSizePreference({
          width: width,
          height: height
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isResizeEnabled]);
  useLayoutEffect(() => {
    if (!isResizeEnabled || isDraggingRef.current || !wrapperRef.current) return;
    if (savedSizePreference) {
      const clamped = clampWidgetSize(Object.assign({}, savedSizePreference, {
        browserWindowWidth,
        browserWindowHeight,
        launcherType
      }));
      wrapperRef.current.style.setProperty('--widget-width', `${clamped.width}px`);
      wrapperRef.current.style.setProperty('--widget-height', `${clamped.height}px`);
    } else {
      wrapperRef.current.style.removeProperty('--widget-width');
      wrapperRef.current.style.removeProperty('--widget-height');
    }
  }, [isResizeEnabled, savedSizePreference, browserWindowWidth, browserWindowHeight, launcherType]);
  const onResizeHandlePointerDown = useCallback(event => {
    var _wrapperRef$current, _chatWindowEl$offsetW, _chatWindowEl$offsetH;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const chatWindowEl = (_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === void 0 ? void 0 : _wrapperRef$current.querySelector(CHAT_WINDOW_CLASS);
    const startWidth = (_chatWindowEl$offsetW = chatWindowEl === null || chatWindowEl === void 0 ? void 0 : chatWindowEl.offsetWidth) !== null && _chatWindowEl$offsetW !== void 0 ? _chatWindowEl$offsetW : defaultWidgetSize.width;
    const startHeight = (_chatWindowEl$offsetH = chatWindowEl === null || chatWindowEl === void 0 ? void 0 : chatWindowEl.offsetHeight) !== null && _chatWindowEl$offsetH !== void 0 ? _chatWindowEl$offsetH : defaultWidgetSize.height;
    const startX = event.screenX;
    const startY = event.screenY;
    isDraggingRef.current = true;
    setIsResizing(true);
    expandIframeToViewportForDrag({
      width: browserWindowWidth,
      height: browserWindowHeight
    });
    const computeSize = (screenX, screenY) => {
      const deltaX = screenX - startX;
      const deltaY = screenY - startY;
      const rawWidth = widgetLocation === RIGHT_ALIGNED ? startWidth - deltaX : startWidth + deltaX;
      const rawHeight = startHeight - deltaY;
      return clampWidgetSize({
        width: rawWidth,
        height: rawHeight,
        browserWindowWidth,
        browserWindowHeight,
        launcherType
      });
    };
    const onPointerMove = ev => {
      const newSize = computeSize(ev.screenX, ev.screenY);
      if (wrapperRef.current) {
        wrapperRef.current.style.setProperty('--widget-width', `${newSize.width}px`);
        wrapperRef.current.style.setProperty('--widget-height', `${newSize.height}px`);
      }
    };
    const onPointerEnd = ev => {
      if (target.hasPointerCapture(ev.pointerId)) {
        target.releasePointerCapture(ev.pointerId);
      }
      const finalSize = computeSize(ev.screenX, ev.screenY);
      isDraggingRef.current = false;
      setIsResizing(false);
      if (ev.type === 'pointerup' && (finalSize.width !== startWidth || finalSize.height !== startHeight)) {
        dispatch(trackInteraction(EVENT_NAMES.RESIZED_WIDGET, {
          resizeType: 'custom',
          widgetWidth: Math.round(finalSize.width),
          widgetHeight: Math.round(finalSize.height)
        }));
      }
      setSavedSizePreference(finalSize);
      postMessageToParent(STORE_WIDGET_SIZE_PREFERENCE, finalSize);
      if (wrapperRef.current) {
        handleIframeResize({
          width: wrapperRef.current.offsetWidth,
          height: wrapperRef.current.offsetHeight
        });
      }
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerEnd);
      target.removeEventListener('pointercancel', onPointerEnd);
    };
    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerup', onPointerEnd);
    target.addEventListener('pointercancel', onPointerEnd);
  }, [widgetLocation, browserWindowHeight, browserWindowWidth, defaultWidgetSize, launcherType, dispatch]);
  const onToggleExpand = useCallback(() => {
    const resizeType = isExpanded ? 'classic' : 'docked';
    const newSize = isExpanded ? defaultWidgetSize : clampWidgetSize({
      width: 500,
      height: Number.MAX_SAFE_INTEGER,
      browserWindowWidth,
      browserWindowHeight,
      launcherType
    });
    setSavedSizePreference(newSize);
    postMessageToParent(STORE_WIDGET_SIZE_PREFERENCE, newSize);
    dispatch(trackInteraction(EVENT_NAMES.RESIZED_WIDGET, {
      resizeType,
      widgetWidth: newSize.width,
      widgetHeight: newSize.height
    }));
  }, [isExpanded, browserWindowWidth, browserWindowHeight, launcherType, defaultWidgetSize, dispatch]);
  const resizeContextValue = useMemo(() => ({
    isResizeEnabled,
    onResizeHandlePointerDown,
    widgetLocation,
    isResizing,
    isExpanded,
    onToggleExpand
  }), [isResizeEnabled, onResizeHandlePointerDown, widgetLocation, isResizing, isExpanded, onToggleExpand]);
  return {
    wrapperRef,
    isDraggingRef,
    resizeContextValue
  };
};